import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, ArrowLeft } from 'lucide-react';
import { BreakDuration } from '../../../types/state/timecards';
import { toast } from 'react-toastify';

interface BreakManagerOverlayProps {
  isOpen: boolean;
  breaks: BreakDuration[];
  editIndex?: number | null;
  onSave: (updatedBreaks: BreakDuration[]) => void;
  onClose: () => void;
}

const BreakManagerOverlay: React.FC<BreakManagerOverlayProps> = ({
  isOpen,
  breaks: initialBreaks,
  editIndex: initialEditIndex,
  onSave,
  onClose
}) => {
  // Always show the form directly - no intermediate page
  const [newBreak, setNewBreak] = useState<BreakDuration>({
    type: 'unpaid',
    duration_minutes: 30
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(initialEditIndex || null);
  
  // Use string-based state for date/time inputs (like AddTimesheetEntry)
  const [startDateTime, setStartDateTime] = useState<string>("");
  const [endDateTime, setEndDateTime] = useState<string>("");
  
  // Toggle between duration input modes - true for start/end time, false for direct duration input
  const [isTimeInputMode, setIsTimeInputMode] = useState<boolean>(true);

  // Reset form when open state or edit index changes
  useEffect(() => {
    if (isOpen) {
      // Initialize with defaults or edited break data
      if (initialEditIndex && initialBreaks && initialBreaks[initialEditIndex]) {
        setNewBreak({ ...initialBreaks[initialEditIndex] });
        setEditingIndex(initialEditIndex);
      } else {
        setNewBreak({
          type: 'unpaid',
          duration_minutes: 30
        });
        setEditingIndex(null);
      }
      
      // Set current date/time as default values (in ISO format)
      const now = new Date();
      const later = new Date(now.getTime() + 30 * 60000); // 30 minutes later
    
      setStartDateTime(now.toISOString().slice(0, 16));
      setEndDateTime(later.toISOString().slice(0, 16));
    }
  }, [isOpen, initialBreaks, initialEditIndex]);

  const validateBreak = (breakData: BreakDuration): { valid: boolean; message?: string } => {
    // Validate duration
    if (breakData.duration_minutes === undefined || breakData.duration_minutes <= 0) {
      return { valid: false, message: 'Break duration must be greater than 0 minutes' };
    }
    
    // Validate that end time is after start time if in time mode
    if (!isTimeInputMode && endDateTime && startDateTime && new Date(endDateTime) <= new Date(startDateTime)) {
      return { valid: false, message: 'End time must be after start time' };
    }
    
    // Validate that duration isn't too long (greater than 8 hours)
    if (breakData.duration_minutes > 480) {
      return { valid: false, message: 'Break duration cannot exceed 8 hours (480 minutes)' };
    }
    
    return { valid: true };
  };
  
  // Format a date for display in the form
  const formatDateDisplay = (date: Date): string => {
    return date.toLocaleString('en-US', {
      day: 'numeric', 
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Function to handle duration changes
  const handleDurationChange = (minutes: number) => {
    // Ensure minutes is within valid range (1 to 480 = 8 hours)
    const validMinutes = Math.min(Math.max(1, minutes), 480);
    
    // Update the break object
    setNewBreak({
      ...newBreak,
      duration_minutes: validMinutes
    });
    
    // Update the end time based on the new duration
    if (startDateTime) {
      const startMs = new Date(startDateTime).getTime();
      const newEndTime = new Date(startMs + validMinutes * 60000);
      setEndDateTime(newEndTime.toISOString().slice(0, 16));
    }
  };

  const handleAddBreak = () => {
    const validation = validateBreak(newBreak);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    const updatedBreaks = [...initialBreaks];
    
    if (editingIndex !== null) {
      // Update existing break
      updatedBreaks[editingIndex] = newBreak;
    } else {
      // Add new break
      updatedBreaks.push(newBreak);
    }

    // Save and close the overlay
    onSave(updatedBreaks);
  };

  const formatBreakTime = (breakData: BreakDuration): string => {
    if (breakData.duration_minutes !== undefined) {
      const hours = Math.floor(breakData.duration_minutes / 60);
      const minutes = breakData.duration_minutes % 60;
      
      if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
      } else if (hours > 0) {
        return `${hours}h`;
      } else {
        return `${minutes}m`;
      }
    }
    return '';
  };
  
  // Handlers for datetime inputs
  const handleStartDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDateTime = e.target.value;
    setStartDateTime(newStartDateTime);
    
    // Update duration based on new start time
    if (endDateTime) {
      const startMs = new Date(newStartDateTime).getTime();
      const endMs = new Date(endDateTime).getTime();
      const durationMs = endMs - startMs;
      const durationMinutes = Math.max(1, Math.round(durationMs / 60000));
      
      setNewBreak({
        ...newBreak,
        duration_minutes: durationMinutes
      });
    }
  };
  
  const handleEndDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDateTime = e.target.value;
    setEndDateTime(newEndDateTime);
    
    // Update duration based on new end time
    if (startDateTime) {
      const startMs = new Date(startDateTime).getTime();
      const endMs = new Date(newEndDateTime).getTime();
      const durationMs = endMs - startMs;
      const durationMinutes = Math.max(1, Math.min(480, Math.round(durationMs / 60000)));
      
      setNewBreak({
        ...newBreak,
        duration_minutes: durationMinutes
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-white">
      <div className="flex justify-between items-center p-4 border-b border-gray-200">
        <button onClick={onClose} className="text-blue-600 flex items-center">
          <ArrowLeft size={18} />
          <span className="ml-1">Back</span>
        </button>
        <h1 className="text-md font-medium">{editingIndex !== null ? 'Edit Break' : 'Add Break'}</h1>
        <div></div> {/* Empty div for flex alignment */}
      </div>

      <div className="flex-1 overflow-auto p-4">
        {/* Toggle for input mode - using same labels as AddTimesheetEntry */}
        <div className="mb-4 flex justify-between items-center">
          <label className="text-sm font-medium">Input Mode</label>
          <div className="flex items-center">
            <span className={`mr-2 text-sm ${!isTimeInputMode ? 'font-medium text-blue-600' : 'text-gray-500'}`}>Duration</span>
            <button
              type="button"
              role="switch"
              aria-checked={isTimeInputMode}
              onClick={() => setIsTimeInputMode(!isTimeInputMode)}
              className={`relative inline-flex items-center h-5 rounded-full w-10 transition-colors ${
                isTimeInputMode ? "bg-blue-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                  isTimeInputMode ? "translate-x-5" : "translate-x-1"
                }`}
              />
            </button>
            <span className={`ml-2 text-sm ${isTimeInputMode ? 'font-medium text-blue-600' : 'text-gray-500'}`}>Hours</span>
          </div>
        </div>

        {isTimeInputMode ? (
          <>
            {/* Hours-based input mode */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Duration (minutes)</label>
              <input 
                type="number"
                min="1"
                max="480"
                value={newBreak.duration_minutes}
                onChange={(e) => handleDurationChange(parseInt(e.target.value) || 30)}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
              />
            </div>

            {/* Duration quick selection - only shown in hours mode */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Quick Select</label>
              <div className="grid grid-cols-4 gap-2">
                <button
                  type="button"
                  onClick={() => handleDurationChange(15)} 
                  className={`px-2 py-1 border rounded-md text-sm ${newBreak.duration_minutes === 15 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
                >
                  15 min
                </button>
                <button
                  type="button"
                  onClick={() => handleDurationChange(30)}
                  className={`px-2 py-1 border rounded-md text-sm ${newBreak.duration_minutes === 30 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
                >
                  30 min
                </button>
                <button
                  type="button"
                  onClick={() => handleDurationChange(45)}
                  className={`px-2 py-1 border rounded-md text-sm ${newBreak.duration_minutes === 45 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
                >
                  45 min
                </button>
                <button
                  type="button"
                  onClick={() => handleDurationChange(60)}
                  className={`px-2 py-1 border rounded-md text-sm ${newBreak.duration_minutes === 60 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
                >
                  60 min
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Time-based input mode - with editable datetime inputs */}
            <div className="mb-4">
              <label className="block text-md font-medium mb-1">Start Time</label>
              <input
                type="datetime-local"
                value={startDateTime}
                onChange={handleStartDateTimeChange}
                className="w-full mt-1 p-3 border border-gray-300 rounded-md text-gray-700"
                placeholder="Start Date & Time"
              />
            </div>

            <div className="mb-4">
              <label className="block text-md font-medium mb-1">End Time</label>
              <input
                type="datetime-local"
                value={endDateTime}
                min={startDateTime}
                onChange={handleEndDateTimeChange}
                className="w-full mt-1 p-3 border border-gray-300 rounded-md text-gray-700"
                placeholder="End Date & Time"
              />
            </div>

            <div className="mb-4">
              <div className="flex justify-between items-center">
                <label className="block text-md font-medium">Duration</label>
                <div className="text-md font-medium">
                  {newBreak.duration_minutes} min
                </div>
              </div>
            </div>
          </>
        )}

        <div className="flex justify-between items-center mb-6">
          <label className="text-sm font-medium">Paid Break</label>
          <button
            type="button"
            role="switch"
            aria-checked={newBreak.type === 'paid'}
            onClick={() => setNewBreak({
              ...newBreak,
              type: newBreak.type === 'paid' ? 'unpaid' : 'paid'
            })}
            className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${
              newBreak.type === 'paid' ? "bg-blue-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                newBreak.type === 'paid' ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      <div className="p-4 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={onClose}
            className="py-2 bg-gray-400 text-white rounded-md font-medium text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAddBreak}
            className="py-2 bg-blue-600 text-white rounded-md font-medium text-sm"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default BreakManagerOverlay;
